import useSWR from "swr";
import VehicleService from "services/VehicleService";
import useDealerId from "hooks/useDealerId";

/**
 * useCategoryLibrary
 * Hidden resolver that executes the vehicle cascade flow silently in the background
 * to satisfy API requirements while presenting a clean Category Library.
 */
export default function useCategoryLibrary() {
    const dlid = useDealerId();
    const catalog = "motor";

    // BACKGROUND CASCADE RESOLUTION
    // 1. Resolve Makes
    const { data: makes = [] } = useSWR(dlid ? ["lib-makes", dlid] : null, 
        () => VehicleService.getMakes({ dlid, catalog }));

    // 2. Resolve Years (Pick first make)
    const makeId = makes[0]?.id;
    const { data: years = [] } = useSWR(dlid && makeId ? ["lib-years", dlid, makeId] : null, 
        () => VehicleService.getYears({ dlid, makeId, catalog }));

    // 3. Resolve Models (Pick first year)
    const yearId = years[0]?.name;
    const { data: models = [] } = useSWR(dlid && makeId && yearId ? ["lib-models", dlid, makeId, yearId] : null, 
        () => VehicleService.getModels({ dlid, makeId, yearId, catalog }));

    // 4. Resolve Trims (Pick first model)
    const modelId = models[0]?.id;
    const { data: trims = [] } = useSWR(dlid && makeId && yearId && modelId ? ["lib-trims", dlid, makeId, yearId, modelId] : null, 
        () => VehicleService.getTrims({ dlid, makeId, yearId, modelId, catalog }));

    // 5. Resolve Engines (Pick first trim)
    const trimId = trims[0]?.id;
    const { data: engines = [] } = useSWR(dlid && makeId && yearId && modelId && trimId ? ["lib-engines", dlid, makeId, yearId, modelId, trimId] : null, 
        () => VehicleService.getEngines({ dlid, makeId, yearId, modelId, trimId, catalog }));

    // FINAL RESOLUTION: Categories
    const engine = engines[0];
    const vtecid = engine?.VehicleToEngineConfigID;
    const engineId = engine?.id;

    const categoryKey = dlid && makeId && yearId && modelId && trimId && engineId 
        ? ["lib-categories-multi", dlid, makeId, yearId, modelId, trimId, engineId, vtecid] 
        : null;
    
    const { data: categories = [], isLoading } = useSWR(categoryKey, async () => {
        const locales = ["en", "es", "pt", "fr"];
        const params = { dlid, makeId, yearId, modelId, trimId, engineId, vtecid, catalog };

        const results = await Promise.all(
            locales.map(loc => VehicleService.getCategories({ ...params, loc }))
        );

        const categoryMap = {};
        locales.forEach((loc, index) => {
            const data = results[index] || [];
            data.forEach(cat => {
                if (!categoryMap[cat.id]) {
                    categoryMap[cat.id] = { id: cat.id, locales: {} };
                }
                categoryMap[cat.id].locales[loc] = {
                    name: cat.name,
                    slug: cat.slug,
                    subcategory_slug: cat.subcategory_slug || cat.slug
                };
            });
        });

        return Object.values(categoryMap);
    });

    // 6. Return combined result with context
    const isResolvingCascade = dlid && !categoryKey;
    const isFetchingCategories = !!categoryKey && isLoading;
    const globalLoading = isResolvingCascade || isFetchingCategories;

    return {
        categories,
        isLoading: globalLoading,
        context: {
            dlid,
            makeId,
            yearId,
            modelId,
            trimId,
            engineId,
            vtecid,
            catalog
        }
    };
}
